import type { Voice } from '@/types/assistant'

/**
 * Pure selection rules for the assistant voices picker, kept out of the
 * components so they can be unit-tested without rendering.
 */

/** The languages an assistant is configured for: default first, extras deduplicated. */
export function assistantLanguageSet(
  defaultLanguage: string,
  additionalLanguages?: string[]
): string[] {
  const extras = (additionalLanguages ?? []).filter((lang) => lang && lang !== defaultLanguage)
  return [defaultLanguage, ...extras]
}

/**
 * Whether a voice declares support for every language in the set. A voice
 * without a `meta.languages` list is unknown rather than unsupported (the
 * backend treats an empty list as unrestricted), so it stays eligible.
 */
export function voiceCoversLanguages(voice: Voice, languages: string[]): boolean {
  const supported = voice.meta?.languages ?? []
  if (supported.length === 0) return true
  return languages.every((lang) => supported.includes(lang))
}

/**
 * Languages the voice sounds native in. Read through a local type so this
 * compiles before and after the shared Voice type gains the field.
 */
function nativeLanguagesOf(voice: Voice): string[] {
  return (voice.meta as { native_languages?: string[] } | undefined)?.native_languages ?? []
}

const baseLanguage = (code: string) => code.split('-')[0].toLowerCase()

export type NativeStatus = 'native' | 'accent' | 'unknown'

/**
 * How the voice sounds in the language, as far as we have curated it:
 * `native` when the language is in `native_languages` (region subtags are
 * lenient, `nl-NL` covers `nl-BE`), `accent` when the list is set but does
 * not include it (a clone outside its training language), `unknown` when the
 * list is empty. Empty is a backend default (no restriction), not a curated
 * claim, so the picker shows nothing for it.
 */
export function nativeStatus(voice: Voice, language: string): NativeStatus {
  const native = nativeLanguagesOf(voice)
  if (native.length === 0) return 'unknown'
  if (native.includes(language)) return 'native'
  const base = baseLanguage(language)
  return native.some((code) => baseLanguage(code) === base) ? 'native' : 'accent'
}

export function isPremiumVoice(voice: Voice): boolean {
  return voice.meta?.pricing?.tier === 'premium'
}

/** Premium (plan gate with a request flow) and not in the current plan. */
export function isPremiumLocked(voice: Voice): boolean {
  return isPremiumVoice(voice) && !voice.available_in_plan
}

/**
 * Non-public without plan access is internal-only (edge: a customer's
 * already-assigned hidden voice) and stays a plain disabled card.
 */
export function isInternalLocked(voice: Voice): boolean {
  return !isPremiumLocked(voice) && !voice.is_public && !voice.available_in_plan
}
